import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { env, resolveWsUrl } from '@/config/env';
import type { ActivityLogEntry, Paginated, SystemHealth, Torrent, WsMessage } from '@/types';
import { useUiStore } from '@/stores/uiStore';
import { queryKeys } from '@/hooks/queryKeys';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const STALE_AFTER_MS = 20000;

/** Applies an incoming WsMessage directly to the TanStack Query cache — no refetch/polling. */
export function applyWsMessage(queryClient: QueryClient, message: WsMessage): void {
  switch (message.type) {
    case 'torrent.update': {
      const torrent = message.data;
      queryClient.setQueryData<Torrent>(queryKeys.torrent(torrent.id), torrent);
      queryClient.setQueriesData<Paginated<Torrent>>({ queryKey: queryKeys.torrentsRoot }, (old) => {
        if (!old) return old;
        const exists = old.items.some((t) => t.id === torrent.id);
        const items = exists
          ? old.items.map((t) => (t.id === torrent.id ? torrent : t))
          : [torrent, ...old.items];
        return { ...old, items, total: exists ? old.total : old.total + 1 };
      });
      break;
    }
    case 'torrent.removed': {
      const { id } = message.data;
      queryClient.removeQueries({ queryKey: queryKeys.torrent(id) });
      queryClient.setQueriesData<Paginated<Torrent>>({ queryKey: queryKeys.torrentsRoot }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((t) => t.id !== id),
          total: Math.max(0, old.total - 1),
        };
      });
      break;
    }
    case 'activity.new': {
      const entry = message.data;
      queryClient.setQueriesData<Paginated<ActivityLogEntry>>(
        { queryKey: queryKeys.activityRoot },
        (old) => {
          if (!old) return old;
          return { ...old, items: [entry, ...old.items].slice(0, old.pageSize), total: old.total + 1 };
        },
      );
      break;
    }
    case 'system.health': {
      queryClient.setQueryData<SystemHealth>(queryKeys.health, message.data);
      break;
    }
    case 'ping':
    case 'pong':
      break;
  }
}

/**
 * Owns the single app-wide WebSocket connection: exponential-backoff
 * reconnect, staleness detection, and dispatches messages straight into
 * the TanStack Query cache. Connection status lives in the Zustand UI
 * store (see docs/architecture/system-architecture.md#2).
 */
export function useWebSocket(): void {
  const queryClient = useQueryClient();
  const setConnectionStatus = useUiStore((s) => s.setConnectionStatus);
  const socketRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const staleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    closedByUsRef.current = false;

    const armStaleTimer = () => {
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      staleTimerRef.current = setTimeout(() => {
        setConnectionStatus('stale');
      }, STALE_AFTER_MS);
    };

    const connect = () => {
      setConnectionStatus('connecting');
      const url = resolveWsUrl(env.wsUrl);
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        backoffRef.current = INITIAL_BACKOFF_MS;
        setConnectionStatus('open');
        armStaleTimer();
      };

      socket.onmessage = (event) => {
        armStaleTimer();
        setConnectionStatus('open');
        try {
          const message = JSON.parse(event.data) as WsMessage;
          applyWsMessage(queryClient, message);
        } catch {
          // ignore malformed frames
        }
      };

      socket.onclose = () => {
        setConnectionStatus('closed');
        if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
        if (closedByUsRef.current) return;
        reconnectTimerRef.current = setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
          connect();
        }, backoffRef.current);
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
